/**
 * Normalise the `/custom/kwick/v1/getInvoices` response into an invoices array,
 * or `null` when there is nothing to show (no invoices / transport failure).
 *
 * The backend is inconsistent: it returns a bare array when invoices exist, but
 * an object `{ message, invoices: [] }` when a site has none. Consumers here
 * (community/member filters, overview aggregation, the Billing list) all assume
 * an array and call `.map()` on it — so the object shape used to reach `Billing`
 * and throw `invoices.map is not a function`, blanking the whole view for any
 * site with no invoices.
 *
 * Anything that isn't a non-empty array of invoices collapses to `null`, so the
 * invoices view renders its existing error/empty state with a Retry action
 * (`Billing` shows it off `kpi.kwickInvoices === null`) instead of a bare
 * "no invoices" message or a blank screen.
 *
 * - Non-empty array        -> returned as-is (success; unchanged behaviour).
 * - `{ invoices: [...] }`  -> the inner array (defensive; backend uses the object only when empty).
 * - Empty / null / other   -> null (no invoices or transport error -> retry state).
 */
export function normalizeInvoicesResponse(data: unknown): any[] | null {
  const invoices = Array.isArray(data)
    ? data
    : data && typeof data === 'object' && Array.isArray((data as any).invoices)
      ? (data as any).invoices
      : null;
  return invoices && invoices.length > 0 ? invoices : null;
}
